import { initializeApp } from "firebase/app";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
} from "firebase/firestore";

// Base Firebase configuration (same for all tenants)

const baseFirebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Validate Firebase configuration

for (const key in baseFirebaseConfig) {
  if (!baseFirebaseConfig[key]) {
    throw new Error(
      `Firebase configuration error: REACT_APP_FIREBASE_${key.toUpperCase()} is not defined in environment variables.`
    );
  }
}

// Tenant-specific configurations
const tenantConfigs = {
  // Default tenant - uses default database and bucket (no password)
  default: {
    databaseId: "(default)",
    storageBucket: "drive-project-60c16.firebasestorage.app",
  },

  // Flamengo tenant - uses specific database and bucket (with password)
  private: {
    databaseId: "drive-bkp",
    storageBucket: "drive-project-backup",
    password: true,
  },

  // Flamengo tenant - uses specific folder and collection prefixes (with password)
  flamengo: {
    databaseId: "(default)", // Mesmo database, mas com prefixos diferentes
    storageBucket: "YOUR_PROJECT_ID.appspot.com",
    password: true,
  },
};

// Cache for Firebase instances
const firebaseInstances = {};

// For security, avoid exposing Firebase instances on the window object

/**
 * Get or create Firebase instance for a specific tenant
 */
// Função para obter ou criar instância Firebase para um tenant específico
export const getFirebaseInstance = (tenant = "default") => {
  // Se já existe uma instância para este tenant, retorna ela
  if (firebaseInstances[tenant]) {
    return firebaseInstances[tenant];
  }

  // Verifica se existe configuração para o tenant
  const tenantConfig = tenantConfigs[tenant];
  if (!tenantConfig) {
    console.warn(
      `Configuração não encontrada para tenant: ${tenant}. Usando configuração padrão.`
    );
    return getFirebaseInstance("default");
  }

  try {
    // Inicializa nova instância Firebase (mesmo projeto, mas identificador único)
    const app = initializeApp(baseFirebaseConfig, `app-${tenant}`);
    const auth = getAuth(app);

    // Storage com bucket específico se configurado
    const storage = getStorage(app, tenantConfig.storageBucket);

    // Firestore com cache persistente em IndexedDB e suporte a múltiplas abas
    let db;
    try {
      db = initializeFirestore(
        app,
        {
          localCache: persistentLocalCache({
            tabManager: persistentMultipleTabManager(),
          }),
        },
        tenantConfig.databaseId,
      );
    } catch (cacheErr) {
      db = getFirestore(app, tenantConfig.databaseId);
    }

    // Connect to Firebase Emulators if in E2E test environment
    if (window.e2e_testing) {
      console.log("Connecting to Firebase Emulators...");
      connectAuthEmulator(auth, "http://127.0.0.1:9099"); // Default Auth emulator port
      connectFirestoreEmulator(db, "127.0.0.1", 8080); // Default Firestore emulator port
      connectStorageEmulator(storage, "127.0.0.1", 9199); // Default Storage emulator port
    }

    // Armazena no cache
    firebaseInstances[tenant] = {
      app,
      auth,
      storage,
      db,
      config: {
        ...baseFirebaseConfig,
        ...tenantConfig,
      },
      tenant,
      databaseId: tenantConfig.databaseId,
      folderPrefix: tenantConfig.folderPrefix,
    };

    console.log(
      `Firebase inicializado para tenant: ${tenant} (Database: ${tenantConfig.databaseId})`
    );
    return firebaseInstances[tenant];
  } catch (error) {
    console.error(`Erro ao inicializar Firebase para tenant ${tenant}:`, error);
    // Em caso de erro, tenta usar a configuração padrão
    if (tenant !== "default") {
      return getFirebaseInstance("default");
    }
    throw error;
  }
};

/**
 * Get list of available tenants
 */
export const getAvailableTenants = () => {
  return Object.keys(tenantConfigs);
};

/**
 * Check if tenant exists
 */
export const tenantExists = (tenant) => {
  return tenantConfigs.hasOwnProperty(tenant);
};

/**
 * Check if tenant requires password
 */
export const tenantRequiresPassword = (tenant) => {
  const config = tenantConfigs[tenant];
  return config && config.password === true;
};

/**
 * Get tenant configuration
 */
export const getTenantConfig = (tenant) => {
  return tenantConfigs[tenant] || tenantConfigs.default;
};
